import { useCallback, useEffect, useState } from 'react';
import { trackSuccessEvent } from '@/helpers/analytics';
import {
  clearOnboardingSessionId,
  getOnboardingSessionId,
} from '@/helpers/onboardingSession';
import { getOnboardingGuest } from '@/helpers/onboardingGuest';
import { api, ApiError } from '@/basedata/apiCall';
import type { UserProfileResponse } from '@/types/profile';

const DEFAULT_ERROR_MESSAGE = 'Unable to load profile summary. Please try again.';

/**
 * Fallback do comprovante quando `GET /user/profile` não acha ninguém para a
 * sessão: é o caso de quem JÁ tinha biometria ao entrar no funil — a sessão da
 * face indexada é a ANTIGA (o service não a sobrescreve, senão a biometria
 * ficaria órfã no portão). Os dados vêm do `/identify`/`/invite` desta visita.
 * Campo vazio não vira linha no resumo — convidado de evento não tem "plano".
 */
function profileFromEnrolledGuest(): UserProfileResponse | null {
  const guest = getOnboardingGuest();
  if (guest === null || !guest.alreadyOnboarded) return null;
  return {
    name: guest.fullName,
    email: guest.email ?? '',
    plan: '',
    createdAt: guest.enrolledAt ?? '',
  };
}

export type UserProfileState =
  | { status: 'loading' }
  | { status: 'success'; profile: UserProfileResponse }
  | { status: 'error'; message: string; httpStatus?: number };

export type UseUserProfileResult = UserProfileState & {
  retry: () => void;
};

function getProfileErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return DEFAULT_ERROR_MESSAGE;
}

export function useUserProfile(): UseUserProfileResult {
  const [state, setState] = useState<UserProfileState>({ status: 'loading' });

  const loadProfile = useCallback(async () => {
    setState({ status: 'loading' });

    try {
      const sessionId = getOnboardingSessionId();
      const profile = await api.user.getProfile(
        sessionId !== null ? { sessionId } : undefined,
      );
      clearOnboardingSessionId();
      setState({ status: 'success', profile });
      void trackSuccessEvent({
        name: 'success_profile_load_success',
        params: { plan: profile.plan },
      });
    } catch (error) {
      // Reentrada de quem já tinha biometria: a face indexada continua na
      // sessão ANTIGA (o service não reamarra), então esta sessão não tem
      // perfil. Cai nos dados que o /identify|/invite desta visita devolveu.
      const enrolled = profileFromEnrolledGuest();
      if (enrolled !== null) {
        clearOnboardingSessionId();
        setState({ status: 'success', profile: enrolled });
        void trackSuccessEvent({
          name: 'success_profile_load_success',
          params: { plan: enrolled.plan },
        });
        return;
      }
      setState({
        status: 'error',
        message: getProfileErrorMessage(error),
        httpStatus: error instanceof ApiError ? error.status : undefined,
      });
      void trackSuccessEvent({
        name: 'success_profile_load_error',
        params: error instanceof ApiError ? { status: error.status } : undefined,
      });
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const retry = useCallback(() => {
    void loadProfile();
  }, [loadProfile]);

  return { ...state, retry };
}
